import { CircularProgress, Grid, Tab, Tabs, Typography } from "@mui/material";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  CandidateDetails,
  JobRequestDetails,
  useGetJobStatsQuery,
} from "../../../redux/Hire/jobRequestApi";
import AssessmentController from "../../HireV3/Assessment/AssessmentController";
import TabPanel from "./TabPanel";

type Props = {
  candidate?: CandidateDetails;
  job?: JobRequestDetails;
};

function assessmentTabAriaProps(index: number) {
  return {
    id: `assessment-tab-${index}`,
    "aria-controls": `assessment-tabpanel-${index}`,
  };
}

function assessmentTabPanelAriaProps(index: number) {
  return {
    id: `assessment-tabpanel-${index}`,
    "aria-labelledby": `assessment-tab-${index}`,
  };
}

const CandidateAssessment = ({ candidate, job }: Props) => {
  const { data: jobStats, isFetching } = useGetJobStatsQuery(
    {
      jobRequestId: job?.jobId || 0,
      version: job?.version || 0,
    },
    {
      skip: !job?.jobId,
    },
  );
  const gridRef = useRef<HTMLDivElement | null>(null);
  const [tabValue, setTabValue] = useState<number>(0);

  const handleTabChange = useCallback(
    (event: React.SyntheticEvent, newTabValue: number) => {
      setTabValue(newTabValue);
    },
    [],
  );

  const [params] = useSearchParams();
  const stage = params.get("stage") || "";

  const noop = useCallback(() => {}, []);

  const tabStages = useMemo(() => {
    return (
      jobStats?.data.filter((el) => el.code?.includes("technicalAssessment")) ||
      []
    );
  }, [jobStats]);

  useEffect(() => {
    if (stage && stage.includes("technicalAssessment")) {
      const matchedStage = tabStages.findIndex((el) => el.code === stage);
      setTabValue(matchedStage > -1 ? matchedStage : 0);
      gridRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [stage, tabStages]);

  if (isFetching || !job) {
    return (
      <Grid container justifyContent="center" alignItems="center" height="100%">
        <CircularProgress />
      </Grid>
    );
  }

  if (tabStages.length === 0) {
    return (
      <Grid container justifyContent="center" alignItems="center" height="100%">
        <Typography fontSize={"14px"} lineHeight={"20px"} color={"#98A2B3"}>
          No assessment stages added for this job request
        </Typography>
      </Grid>
    );
  }

  return (
    <Grid container item wrap="nowrap" gap={4}>
      <Grid item xs={2}>
        <Tabs
          orientation="vertical"
          value={tabValue}
          onChange={handleTabChange}
          aria-label="Assessment Tabs"
          TabIndicatorProps={{
            sx: {
              display: "none",
            },
          }}
        >
          {tabStages.map((stage, index) => (
            <Tab
              key={index}
              label={stage.name}
              sx={{
                "&.Mui-selected": {
                  backgroundColor: "#f0f0f0",
                  borderRadius: "8px",
                },
                minHeight: "32px",
              }}
              {...assessmentTabAriaProps(index)}
            />
          ))}
        </Tabs>
      </Grid>
      <Grid container item xs={10} ref={gridRef}>
        {tabStages.map((stage, index) => (
          <TabPanel
            key={index}
            tabValue={tabValue}
            index={index}
            width={"100%"}
            {...assessmentTabPanelAriaProps(index)}
          >
            <AssessmentController
              job={job}
              stage={stage.code || ""}
              setSelectedStage={noop}
              candidateDetail={candidate}
            />
          </TabPanel>
        ))}
      </Grid>
    </Grid>
  );
};

export default CandidateAssessment;
